/** MapLibre map with cached GeoJSON sources and toggleable presentation layers. */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { MapToolbar } from "./MapToolbar";
import { useLanguageContext } from "../hooks/useLanguage";
import {
  MAX_VISIBLE_MEASURE_SLOTS,
  SLOT_SPACING_PX,
  ZOOM_BREAKPOINTS,
} from "../constants/mapZoom";
import { splitLineByMultiPolygon } from "../utils/geometry";

import type {
  GeoJSONFeatureCollection,
  GeoJSONGeometry,
  LayerVisibility,
  MapMode,
  RegionConfig,
} from "../types";

interface MapProps {
  config: RegionConfig;
  focusData: GeoJSONFeatureCollection | null;
  speciesData: GeoJSONFeatureCollection | null;
  measuresData: GeoJSONFeatureCollection | null;
  /** Habitat lines with no measure allowed under the current filter — drawn as
   * red dashed "no work permitted" lines. */
  noWorkData: GeoJSONFeatureCollection | null;
  layersData: Record<string, GeoJSONFeatureCollection>;
  mode: MapMode;
  visibility: LayerVisibility;
  selectedSpeciesCount?: number;
  highlightMeasureId: string | null;
  lineOverlayMeasureId: string | null;
  highlightNoWork: boolean;
  lineOverlayNoWork: boolean;
  selectionMode: "none" | "polygon" | "viewport";
  polygonPoints: Array<[number, number]>;
  polygonClosed: boolean;
  viewportPolygon: number[][] | null;
  onPolygonPointAdd: (lng: number, lat: number) => void;
  onPolygonFinish: () => void;
  onPolygonUndo: () => void;
  onSelectionClear: () => void;
  onViewportCapture: (bounds: { west: number; south: number; east: number; north: number }) => void;
  onZoomChange: (zoom: number) => void;
  onMeasureClick: (feature: GeoJSONFeatureCollection["features"][number]) => void;
  /** Preview mode for the export modal: a stripped-down, non-interactive
   * second instance. No nav/3D controls, no click/keyboard selection — just
   * pan + zoom so the user can frame a printable extent. */
  preview?: boolean;
  /** Initial camera for a preview instance — copied from the live map so the
   * modal opens showing the same view the user was looking at. */
  initialCenter?: [number, number] | null;
  initialZoom?: number | null;
  /** Fired (preview only) on load and after every pan/zoom, so the export
   * modal can recompute its viewport-scoped legend. */
  onBoundsChange?: (bounds: { west: number; south: number; east: number; north: number }) => void;
  /** Basemap: street (OSM) or satellite imagery. On the live map this is
   * driven by the live map toolbar; 3D terrain always forces satellite
   * regardless. On preview instances it's set by the export modal. */
  basemap?: "street" | "satellite";
  /** Fired from the live map toolbar when the user switches the basemap. */
  onBasemapChange?: (basemap: "street" | "satellite") => void;
  /** Controlled basemap raster opacity (0.2–1). Used by the export modal to
   * drive a preview instance's opacity; when omitted the map manages its own
   * opacity via the live toolbar slider. */
  mapOpacity?: number;
  /** Other municipality/stadt marker points for focus switching. Live map only. */
  municipalityLocations?: GeoJSONFeatureCollection | null;
  /** Boundary preview for the currently hovered/tapped municipality marker. */
  municipalityPreviewData?: GeoJSONFeatureCollection | null;
  onMunicipalityPreview?: (municipality: string | null) => void;
  onMunicipalitySelect?: (municipality: string) => void;
}

const OSM_RASTER_STYLE = {
  version: 8 as const,
  // Self-hosted glyphs (see public/fonts) so symbol layers — currently the
  // waterway name labels — can render text. Bundled rather than pulled from a
  // font CDN to keep the app self-contained behind the nginx proxy.
  glyphs: "/fonts/{fontstack}/{range}.pbf",
  sources: {
    osm: {
      type: "raster" as const,
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [
    {
      id: "osm-tiles",
      type: "raster" as const,
      source: "osm",
      paint: {
        "raster-opacity": 1,
        "raster-saturation": -0.65,
        "raster-contrast": -0.12,
        "raster-brightness-max": 0.94,
      },
    },
  ],
};

const TERRAIN_SOURCE_ID = "terrarium-dem";
const TERRARIUM_TILE_URL = "https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png";
const TERRAIN_EXAGGERATION = 1.5;
const TERRAIN_PITCH = 60;
const TERRAIN_MIN_PITCH = 0;
const TERRAIN_MAX_PITCH = 85;

// The focus municipality outline is dark green against the OSM basemap, but
// that's invisible against satellite imagery — swap to white in 3D.
const FOCUS_OUTLINE_COLOR_2D = "#173F35";
const FOCUS_OUTLINE_COLOR_3D = "#FFFFFF";

const MUNICIPALITY_MARKER_IMAGE_ID = "municipality-marker";
const MUNICIPALITY_MARKERS_SOURCE_ID = "municipality-locations";
const MUNICIPALITY_CLUSTER_LAYER_ID = "municipality-location-clusters";
const MUNICIPALITY_MARKER_LAYER_ID = "municipality-location-markers";
const MUNICIPALITY_PREVIEW_SOURCE_ID = "municipality-preview";
const MUNICIPALITY_PREVIEW_FILL_LAYER_ID = "municipality-preview-fill";
const MUNICIPALITY_PREVIEW_OUTLINE_LAYER_ID = "municipality-preview-outline";

const EMPTY_FEATURE_COLLECTION: GeoJSONFeatureCollection = {
  type: "FeatureCollection",
  features: [],
};

// Unofficial Google satellite XYZ endpoints — not covered by a Maps API key
// or billing agreement, so they can be throttled/blocked without notice.
// Swap for a licensed provider if that becomes a problem.
const SATELLITE_SOURCE_ID = "satellite";
const SATELLITE_LAYER_ID = "satellite-tiles";
const SATELLITE_TILE_URLS = [
  "https://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
  "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
  "https://mt2.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
  "https://mt3.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
];

type MeasurementPoint = [number, number];

const MEASUREMENT_SOURCE_ID = "distance-measurement";
const MEASUREMENT_LINE_CASING_LAYER_ID = "distance-measurement-line-casing";
const MEASUREMENT_LINE_LAYER_ID = "distance-measurement-line";
const MEASUREMENT_PREVIEW_LAYER_ID = "distance-measurement-preview";
const MEASUREMENT_VERTEX_LAYER_ID = "distance-measurement-vertices";
const MEASUREMENT_LAYER_IDS = [
  MEASUREMENT_LINE_CASING_LAYER_ID,
  MEASUREMENT_LINE_LAYER_ID,
  MEASUREMENT_PREVIEW_LAYER_ID,
  MEASUREMENT_VERTEX_LAYER_ID,
];

/** Bottom-left indicator that rotates to always point true north. Only ever
 * moves once 3D mode enables map rotation — otherwise bearing stays at 0. */
class NorthArrowControl implements maplibregl.IControl {
  private map: maplibregl.Map | undefined;
  private container: HTMLDivElement;
  private arrow: HTMLDivElement;

  private handleRotate = (): void => {
    if (!this.map) return;
    this.arrow.style.transform = `rotate(${-this.map.getBearing()}deg)`;
  };

  constructor() {
    this.container = document.createElement("div");
    this.container.className =
      "maplibregl-ctrl flex h-[29px] w-[29px] items-center justify-center rounded-md bg-white shadow";
    this.arrow = document.createElement("div");
    this.arrow.setAttribute("aria-hidden", "true");
    this.arrow.style.transition = "transform 0.2s ease-out";
    this.arrow.innerHTML =
      '<svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="#173F35" stroke-width="1.6">' +
      '<path d="M10 2 L10 18 M10 2 L6 8 M10 2 L14 8" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<text x="10" y="17.5" font-size="5.5" text-anchor="middle" fill="#173F35" stroke="none">N</text>' +
      "</svg>";
    this.container.appendChild(this.arrow);
  }

  onAdd(map: maplibregl.Map): HTMLElement {
    this.map = map;
    map.on("rotate", this.handleRotate);
    this.handleRotate();
    return this.container;
  }

  onRemove(): void {
    this.map?.off("rotate", this.handleRotate);
    this.container.remove();
    this.map = undefined;
  }
}

/** Top-left slider for manual tilt control. Only added while 3D is on — pitch
 * is locked to 0 in 2D, so a tilt slider has nothing to do there. */
class TiltControl implements maplibregl.IControl {
  private map: maplibregl.Map | undefined;
  private container: HTMLDivElement;
  private input: HTMLInputElement;

  private handlePitch = (): void => {
    if (!this.map) return;
    const pitch = String(Math.round(this.map.getPitch()));
    if (this.input.value !== pitch) this.input.value = pitch;
  };

  constructor(label: string) {
    this.container = document.createElement("div");
    // `flima-tilt-ctrl` is our own stable hook so the language-reactivity effect
    // can re-label this control when the user switches language mid-session.
    this.container.className =
      "flima-tilt-ctrl maplibregl-ctrl flex h-[29px] items-center gap-1.5 rounded-md bg-white px-2 shadow";
    this.container.title = label;

    this.input = document.createElement("input");
    this.input.type = "range";
    this.input.min = String(TERRAIN_MIN_PITCH);
    this.input.max = String(TERRAIN_MAX_PITCH);
    this.input.step = "1";
    this.input.value = String(TERRAIN_PITCH);
    this.input.setAttribute("aria-label", label);
    this.input.className = "h-1 w-20 accent-flima-spruce";
    this.input.addEventListener("input", () => {
      this.map?.setPitch(Number(this.input.value));
    });

    this.container.appendChild(this.input);
  }

  onAdd(map: maplibregl.Map): HTMLElement {
    this.map = map;
    map.on("pitch", this.handlePitch);
    this.handlePitch();
    return this.container;
  }

  onRemove(): void {
    this.map?.off("pitch", this.handlePitch);
    this.container.remove();
    this.map = undefined;
  }
}

/** Small "data currency" note shown next to the OSM attribution control,
 * making clear that only known occurrences are mapped. */
class DataDisclaimerControl implements maplibregl.IControl {
  private container: HTMLDivElement;

  constructor(text: string) {
    this.container = document.createElement("div");
    // Compact by default so it doesn't crowd the map; on hover it grows to a
    // comfortably readable size (and widens so the longer text doesn't wrap
    // into a tall block).
    this.container.className =
      "maplibregl-ctrl max-w-[260px] rounded bg-white/85 px-2 py-1 text-right text-xs leading-tight text-flima-slate shadow-sm transition-all duration-150 hover:max-w-[360px] hover:bg-white hover:text-sm hover:shadow motion-reduce:transition-none";
    this.container.textContent = text;
  }

  onAdd(): HTMLElement {
    return this.container;
  }

  onRemove(): void {
    this.container.remove();
  }
}

/** Re-apply translated labels to the built-in NavigationControl buttons and our
 * custom tilt slider. MapLibre only reads its locale when a control is created,
 * so we patch the DOM whenever the language changes or a control is recreated
 * (e.g. on a 3D toggle). Class names are MapLibre's stable hooks; the tilt one
 * is ours. Both `title` (tooltip) and `aria-label` carry each string. */
function relabelMapControls(
  container: HTMLElement,
  labels: { zoomIn: string; zoomOut: string; resetBearing: string; tilt: string },
): void {
  const set = (selector: string, label: string) => {
    const el = container.querySelector<HTMLElement>(selector);
    if (!el) return;
    el.title = label;
    el.setAttribute("aria-label", label);
  };
  set(".maplibregl-ctrl-zoom-in", labels.zoomIn);
  set(".maplibregl-ctrl-zoom-out", labels.zoomOut);
  set(".maplibregl-ctrl-compass", labels.resetBearing);
  const tilt = container.querySelector<HTMLElement>(".flima-tilt-ctrl");
  if (tilt) {
    tilt.title = labels.tilt;
    tilt.querySelector("input")?.setAttribute("aria-label", labels.tilt);
  }
}

function extendCoordinates(bounds: maplibregl.LngLatBounds, coordinates: unknown): void {
  if (!Array.isArray(coordinates)) return;
  if (
    coordinates.length >= 2 &&
    typeof coordinates[0] === "number" &&
    typeof coordinates[1] === "number"
  ) {
    bounds.extend([coordinates[0], coordinates[1]]);
    return;
  }
  coordinates.forEach((coordinate) => extendCoordinates(bounds, coordinate));
}

function extendGeometry(bounds: maplibregl.LngLatBounds, geometry: GeoJSONGeometry): void {
  if (geometry.type === "GeometryCollection") {
    geometry.geometries.forEach((item) => extendGeometry(bounds, item));
    return;
  }
  extendCoordinates(bounds, geometry.coordinates);
}

function getDataBounds(data: GeoJSONFeatureCollection) {
  const bounds = new maplibregl.LngLatBounds();
  data.features.forEach((feature) => extendGeometry(bounds, feature.geometry));
  return bounds.isEmpty() ? null : bounds;
}

function createMunicipalityMarkerImage(): ImageData {
  const size = 48;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return new ImageData(size, size);

  const cx = size / 2;
  ctx.fillStyle = "#1A6B5A";
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(cx, 44);
  ctx.quadraticCurveTo(8, 27, 8, 18);
  ctx.arc(cx, 18, 12, Math.PI, 0, false);
  ctx.quadraticCurveTo(40, 27, cx, 44);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.arc(cx, 18, 5, 0, Math.PI * 2);
  ctx.fill();

  return ctx.getImageData(0, 0, size, size);
}

function municipalityNameFromFeature(
  feature: { properties?: Record<string, unknown> } | undefined,
): string | null {
  const name = feature?.properties?.name;
  return typeof name === "string" && name.length > 0 ? name : null;
}

/** The marker's stable identity (sent to the parent on hover/select), distinct
 * from its display name — names collide once the region expands. */
function municipalityUidFromFeature(
  feature: { properties?: Record<string, unknown> } | undefined,
): string | null {
  const uid = feature?.properties?.uid;
  return typeof uid === "string" && uid.length > 0 ? uid : null;
}

function raiseMunicipalityLayers(map: maplibregl.Map): void {
  [
    MUNICIPALITY_PREVIEW_FILL_LAYER_ID,
    MUNICIPALITY_PREVIEW_OUTLINE_LAYER_ID,
    MUNICIPALITY_CLUSTER_LAYER_ID,
    MUNICIPALITY_MARKER_LAYER_ID,
  ].forEach((layerId) => {
    if (map.getLayer(layerId)) map.moveLayer(layerId);
  });
}

function raiseMeasurementLayers(map: maplibregl.Map): void {
  MEASUREMENT_LAYER_IDS.forEach((layerId) => {
    if (map.getLayer(layerId)) map.moveLayer(layerId);
  });
}

function distanceBetweenMeters(a: MeasurementPoint, b: MeasurementPoint): number {
  return new maplibregl.LngLat(a[0], a[1]).distanceTo(new maplibregl.LngLat(b[0], b[1]));
}

function totalDistanceMeters(points: MeasurementPoint[]): number {
  return points.reduce((total, point, index) => {
    if (index === 0) return total;
    return total + distanceBetweenMeters(points[index - 1], point);
  }, 0);
}

function lastSegmentMeters(points: MeasurementPoint[]): number {
  if (points.length < 2) return 0;
  return distanceBetweenMeters(points[points.length - 2], points[points.length - 1]);
}

function measurementGeoJson(
  points: MeasurementPoint[],
  hoverPoint: MeasurementPoint | null,
  active: boolean,
): GeoJSONFeatureCollection {
  const features: GeoJSONFeatureCollection["features"] = [];
  if (points.length >= 2) {
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: points },
      properties: { kind: "line" },
    });
  }
  if (active && hoverPoint && points.length >= 1) {
    features.push({
      type: "Feature",
      geometry: { type: "LineString", coordinates: [points[points.length - 1], hoverPoint] },
      properties: { kind: "preview" },
    });
  }
  points.forEach((point, index) => {
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: point },
      properties: { kind: "vertex", index: index + 1 },
    });
  });
  return { type: "FeatureCollection", features };
}

// Habitat lines are clipped to the municipality boundary plus a 500m buffer
// (see backend/api/spatial.py), so some legitimately extend past the exact
// boundary. Fade the parts that fall outside instead of hiding them. A
// whole-feature `within` check isn't enough here: a single habitat line can
// cross the boundary, and MapLibre's `within` expression only evaluates the
// feature as a whole, so a partially-inside line would be faded entirely.
// Instead each line is pre-split into inside/outside runs (see
// splitLineByMultiPolygon) and each run carries its own `in_focus` flag that
// the paint expression below reads per-feature.
const FOCUS_LINE_OPACITY_INSIDE = 0.95;
const FOCUS_LINE_OPACITY_OUTSIDE = 0.35;
const FOCUS_LINE_OPACITY_EXPRESSION = [
  "case",
  ["get", "in_focus"],
  FOCUS_LINE_OPACITY_INSIDE,
  FOCUS_LINE_OPACITY_OUTSIDE,
] as unknown as maplibregl.ExpressionSpecification;

/** Flatten the focus municipality's Polygon/MultiPolygon feature(s) into a
 *  flat list of polygons (each polygon's first ring is its outer boundary,
 *  remaining rings are holes), as expected by pointInMultiPolygon. */
function focusPolygonCoordinates(focusData: GeoJSONFeatureCollection): number[][][][] {
  const polygons: number[][][][] = [];
  focusData.features.forEach((feature) => {
    if (feature.geometry.type === "Polygon") {
      polygons.push(feature.geometry.coordinates as number[][][]);
    } else if (feature.geometry.type === "MultiPolygon") {
      polygons.push(...(feature.geometry.coordinates as number[][][][]));
    }
  });
  return polygons;
}

/** Re-tag (and split, where a line crosses the boundary) every line
 *  feature with an `in_focus` property so the layer's paint expression can
 *  fade the parts that lie outside the focus municipality. */
function withFocusTags(
  lineData: GeoJSONFeatureCollection,
  focusData: GeoJSONFeatureCollection | null,
): GeoJSONFeatureCollection {
  const multiPolygon = focusData ? focusPolygonCoordinates(focusData) : [];
  if (multiPolygon.length === 0) {
    return {
      type: "FeatureCollection",
      features: lineData.features.map((feature) => ({
        ...feature,
        properties: { ...feature.properties, in_focus: true },
      })),
    };
  }

  const features: GeoJSONFeatureCollection["features"] = [];
  lineData.features.forEach((feature) => {
    splitLineByMultiPolygon(feature.geometry, multiPolygon).forEach((run) => {
      if (run.coordinates.length < 2) return;
      features.push({
        type: "Feature",
        geometry: { type: "LineString", coordinates: run.coordinates },
        properties: { ...feature.properties, in_focus: run.inside },
      });
    });
  });
  return { type: "FeatureCollection", features };
}

export const Map = memo(function Map({
  config,
  focusData,
  speciesData,
  measuresData,
  noWorkData,
  layersData,
  mode,
  visibility,
  selectedSpeciesCount = config.species.length,
  highlightMeasureId,
  lineOverlayMeasureId,
  highlightNoWork,
  lineOverlayNoWork,
  selectionMode,
  polygonPoints,
  polygonClosed,
  viewportPolygon,
  onPolygonPointAdd,
  onPolygonFinish,
  onPolygonUndo,
  onSelectionClear,
  onZoomChange,
  onMeasureClick,
  preview = false,
  initialCenter = null,
  initialZoom = null,
  onBoundsChange,
  basemap = "street",
  onBasemapChange,
  mapOpacity: controlledMapOpacity,
  municipalityLocations = null,
  municipalityPreviewData = null,
  onMunicipalityPreview,
  onMunicipalitySelect,
}: MapProps) {
  const { t } = useLanguageContext();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const onBoundsChangeRef = useRef(onBoundsChange);
  const onMeasureClickRef = useRef(onMeasureClick);
  const onPolygonPointAddRef = useRef(onPolygonPointAdd);
  const onPolygonFinishRef = useRef(onPolygonFinish);
  const onPolygonUndoRef = useRef(onPolygonUndo);
  const onSelectionClearRef = useRef(onSelectionClear);
  const onZoomChangeRef = useRef(onZoomChange);
  const onMunicipalityPreviewRef = useRef(onMunicipalityPreview);
  const onMunicipalitySelectRef = useRef(onMunicipalitySelect);
  const selectionModeRef = useRef(selectionMode);
  const fittedFocusRef = useRef("");
  const pendingTouchMunicipalityRef = useRef<string | null>(null);
  const municipalityPopupRef = useRef<maplibregl.Popup | null>(null);
  // Hovered measure line (feature-state driver + follow-cursor "click for
  // details" tooltip), so it's obvious the planning lines are clickable.
  const hoveredMeasureFeatureIdRef = useRef<string | number | null>(null);
  const measureTooltipRef = useRef<maplibregl.Popup | null>(null);
  const measureTooltipTextRef = useRef("");
  const navControlRef = useRef<maplibregl.NavigationControl | null>(null);
  const tiltControlRef = useRef<TiltControl | null>(null);
  const measurementActiveRef = useRef(false);
  const measurementPointsRef = useRef<MeasurementPoint[]>([]);
  const [mapReady, setMapReady] = useState(false);
  const [styleReady, setStyleReady] = useState(false);
  const [is3D, setIs3D] = useState(false);
  const [internalMapOpacity, setInternalMapOpacity] = useState(1);
  // The export modal drives a preview instance's opacity via the controlled
  // prop; the live map manages its own via the toolbar slider.
  const mapOpacity = controlledMapOpacity ?? internalMapOpacity;
  const [measurementActive, setMeasurementActive] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState<MeasurementPoint[]>([]);
  const [measurementHover, setMeasurementHover] = useState<MeasurementPoint | null>(null);
  const measurementTotalMeters = useMemo(
    () => totalDistanceMeters(measurementPoints),
    [measurementPoints],
  );
  const measurementLastSegmentMeters = useMemo(
    () => lastSegmentMeters(measurementPoints),
    [measurementPoints],
  );

  useEffect(() => {
    onBoundsChangeRef.current = onBoundsChange;
    onMeasureClickRef.current = onMeasureClick;
    onPolygonPointAddRef.current = onPolygonPointAdd;
    onPolygonFinishRef.current = onPolygonFinish;
    onPolygonUndoRef.current = onPolygonUndo;
    onSelectionClearRef.current = onSelectionClear;
    onZoomChangeRef.current = onZoomChange;
    onMunicipalityPreviewRef.current = onMunicipalityPreview;
    onMunicipalitySelectRef.current = onMunicipalitySelect;
    selectionModeRef.current = selectionMode;
  }, [onBoundsChange, onMeasureClick, onMunicipalityPreview, onMunicipalitySelect, onPolygonPointAdd, onPolygonFinish, onPolygonUndo, onSelectionClear, onZoomChange, selectionMode]);

  useEffect(() => {
    measurementActiveRef.current = measurementActive;
    measurementPointsRef.current = measurementPoints;
  }, [measurementActive, measurementPoints]);

  // Keep the measure-hover tooltip text current across language switches — the
  // map event handlers are registered once and read this ref, not `t` directly.
  useEffect(() => {
    measureTooltipTextRef.current = t("click_for_details");
    if (measureTooltipRef.current?.isOpen()) {
      measureTooltipRef.current.setText(measureTooltipTextRef.current);
    }
  }, [t]);

  const addMeasurementPoint = useCallback((lng: number, lat: number) => {
    setMeasurementPoints((previous) => [...previous, [lng, lat]]);
  }, []);

  const finishMeasurement = useCallback(() => {
    setMeasurementActive(false);
    setMeasurementHover(null);
  }, []);

  const undoMeasurementPoint = useCallback(() => {
    setMeasurementPoints((previous) => previous.slice(0, -1));
    setMeasurementHover(null);
  }, []);

  const clearMeasurement = useCallback(() => {
    setMeasurementActive(false);
    setMeasurementHover(null);
    setMeasurementPoints([]);
  }, []);

  const handleMeasurementToggle = useCallback(() => {
    const shouldActivate = !measurementActive;
    if (shouldActivate && selectionModeRef.current !== "none") {
      onSelectionClearRef.current();
    }
    setMeasurementHover(null);
    setMeasurementActive(shouldActivate);
  }, [measurementActive]);

  const handleBasemapChange = useCallback(
    (nextBasemap: "street" | "satellite") => {
      if (is3D) return;
      onBasemapChange?.(nextBasemap);
    },
    [is3D, onBasemapChange],
  );

  const handleToggle3D = useCallback(() => {
    setIs3D((previous) => !previous);
  }, []);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container || mapRef.current) return;

    const [west, south, east, north] = config.region.bbox;
    const map = new maplibregl.Map({
      container,
      style: OSM_RASTER_STYLE,
      center: initialCenter ?? [(west + east) / 2, (south + north) / 2],
      zoom: initialZoom ?? 10,
      // Preview maps live inside a modal the user already opted into — the
      // cooperative-gesture scroll guard would just get in the way there.
      cooperativeGestures: !preview,
      // Hide the default attribution control on preview maps — the export
      // modal draws its own WYSIWYG overlays and the OSM credit would overlap
      // the legend box at the bottom-right.
      ...(preview ? { attributionControl: false as const } : {}),
      locale: {
        "CooperativeGesturesHandler.WindowsHelpText": t("map_gesture_hint_windows"),
        "CooperativeGesturesHandler.MacHelpText": t("map_gesture_hint_mac"),
        "CooperativeGesturesHandler.MobileHelpText": t("map_gesture_hint_mobile"),
        "NavigationControl.ZoomIn": t("map_zoom_in"),
        "NavigationControl.ZoomOut": t("map_zoom_out"),
        "NavigationControl.ResetBearing": t("map_reset_bearing"),
      },
      preserveDrawingBuffer: true,
      // Flat two-axis view by default — no tilt/rotate until 3D is switched on.
      dragRotate: false,
      touchPitch: false,
      pitchWithRotate: true,
      // MapLibre defaults maxPitch to 60 — raise it so the tilt slider's
      // full 0–85 range actually has somewhere to go.
      minPitch: TERRAIN_MIN_PITCH,
      maxPitch: TERRAIN_MAX_PITCH,
    });
    map.touchZoomRotate.disableRotation();
    const resizeObserver = new ResizeObserver(() => map.resize());
    resizeObserver.observe(container);

    // Live map gets scale + north controls (and its nav control from the 3D
    // effect). Preview instances show none of MapLibre's built-in controls —
    // the export modal draws its own WYSIWYG scale/north/legend overlays, and
    // framing is by scroll/drag — so nothing duplicates or overlaps them.
    if (!preview) {
      map.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: "metric" }), "bottom-left");
      map.addControl(new NorthArrowControl(), "bottom-left");
      map.addControl(
        new DataDisclaimerControl(
          `${t("data_stamp_label")}: ${config.branding.data_stamp}. ${t("data_disclaimer")}`,
        ),
        "bottom-right",
      );
    }
    map.once("load", () => {
      setMapReady(true);
      // Style might still be loading — wait for it
      if (map.isStyleLoaded()) {
        setStyleReady(true);
      } else {
        map.once("styledata", () => setStyleReady(true));
      }
    });

    // Report zoom changes to parent
    const handleZoom = () => onZoomChangeRef.current(map.getZoom());
    map.on("zoomend", handleZoom);
    map.once("load", () => onZoomChangeRef.current(map.getZoom()));

    // Preview: report the framed extent so the export modal can rebuild its
    // viewport-scoped legend live as the user pans/zooms.
    if (preview) {
      const reportBounds = () => {
        const b = map.getBounds();
        onBoundsChangeRef.current?.({
          west: b.getWest(), south: b.getSouth(),
          east: b.getEast(), north: b.getNorth(),
        });
      };
      map.on("moveend", reportBounds);
      map.once("load", reportBounds);
    }

    mapRef.current = map;
    // Store map instance on container for external access (viewport capture,
    // and the export modal reading this preview instance for its PDF snapshot).
    (container as HTMLElement & { __maplibreMap?: maplibregl.Map }).__maplibreMap = map;

    return () => {
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
      navControlRef.current = null;
      tiltControlRef.current = null;
    };
    // initialCenter/initialZoom/preview are fixed for an instance's lifetime
    // (the modal mounts a fresh map each open), so they're intentionally read
    // once at mount and left out of the dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.region.bbox]);

  // Keep MapLibre's built-in control labels in the current language. MapLibre
  // bakes these strings into its DOM once, from the `locale` we pass at
  // construction — it has no public setLocale in this version — so a later
  // language switch would otherwise leave them stuck in whatever language was
  // active at mount. We patch the relevant nodes directly (their class names
  // are the library's stable CSS hooks; `.flima-tilt-ctrl` is our own).
  useEffect(() => {
    const map = mapRef.current;
    if (preview || !map || !mapReady) return;
    const container = map.getContainer();

    // Cooperative-gesture scroll overlay ("Strg + Scrollen…").
    const desktop = container.querySelector<HTMLElement>(".maplibregl-desktop-message");
    const mobile = container.querySelector<HTMLElement>(".maplibregl-mobile-message");
    if (desktop) {
      // Mirror MapLibre's own Mac detection so the desktop hint names the right
      // modifier key (⌘ vs Ctrl).
      const isMac = navigator.userAgent.indexOf("Mac") !== -1;
      desktop.textContent = t(isMac ? "map_gesture_hint_mac" : "map_gesture_hint_windows");
    }
    if (mobile) mobile.textContent = t("map_gesture_hint_mobile");

    relabelMapControls(container, {
      zoomIn: t("map_zoom_in"),
      zoomOut: t("map_zoom_out"),
      resetBearing: t("map_reset_bearing"),
      tilt: t("map_tilt"),
    });
  }, [t, mapReady, preview]);

  // 3D toggle: swaps the nav control (compass only makes sense once rotation
  // is possible), enables terrain + tilt/rotate interaction, and eases the
  // camera in/out of the 3D view. Skipped entirely for preview instances,
  // which are locked to a flat 2D pan/zoom view. Driven only by is3D —
  // satellite-imagery visibility (which also depends on the basemap toggle)
  // is handled by its own effect below, so switching basemap alone never
  // tears down/recreates these controls or eases the camera.
  useEffect(() => {
    const map = mapRef.current;
    if (preview || !map || !mapReady || !styleReady) return;

    try {
      if (!map.getSource(TERRAIN_SOURCE_ID)) {
        map.addSource(TERRAIN_SOURCE_ID, {
          type: "raster-dem",
          tiles: [TERRARIUM_TILE_URL],
          tileSize: 256,
          maxzoom: 15,
          encoding: "terrarium",
        });
      }

      // Remove top-left controls (if present) before re-adding so the stacking
      // order stays stable as the compass/tilt affordances change with 3D.
      if (navControlRef.current) {
        map.removeControl(navControlRef.current);
        navControlRef.current = null;
      }
      if (tiltControlRef.current) {
        map.removeControl(tiltControlRef.current);
        tiltControlRef.current = null;
      }

      const navControl = new maplibregl.NavigationControl({
        showCompass: is3D,
        showZoom: true,
        visualizePitch: is3D,
      });
      navControlRef.current = navControl;
      map.addControl(navControl, "top-left");

      if (is3D) {
        map.dragRotate.enable();
        map.touchPitch.enable();
        map.touchZoomRotate.enableRotation();
        map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration: TERRAIN_EXAGGERATION });
        map.easeTo({ pitch: TERRAIN_PITCH, duration: 650 });

        // Tilt slider only makes sense once pitch is actually adjustable.
        tiltControlRef.current = new TiltControl(t("map_tilt"));
        map.addControl(tiltControlRef.current, "top-left");
      } else {
        map.setTerrain(null);
        map.easeTo({ pitch: 0, bearing: 0, duration: 650 });
        map.dragRotate.disable();
        map.touchPitch.disable();
        map.touchZoomRotate.disableRotation();
      }

      // The freshly created NavigationControl reads MapLibre's mount-time locale,
      // so re-apply the current language's labels (also covers the tilt slider
      // just added above).
      relabelMapControls(map.getContainer(), {
        zoomIn: t("map_zoom_in"),
        zoomOut: t("map_zoom_out"),
        resetBearing: t("map_reset_bearing"),
        tilt: t("map_tilt"),
      });
    } catch (e) {
      // Style may not be loaded during HMR — ignore
    }
  }, [is3D, mapReady, preview, styleReady]);

  // Satellite imagery visibility — live map and preview instances alike.
  // Satellite shows whenever 3D terrain is on (live map only; it reads better
  // tilted) or the user picked the satellite basemap (via the live toolbar,
  // or the export modal for previews). Split from the 3D-toggle effect above
  // so switching basemap alone never tears down/recreates the nav controls or
  // eases the camera. Inserted below "osm-tiles" (not appended, which would
  // default to the top) so it never covers the species/measure layers — only
  // the two basemap layers swap visibility.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady) return;
    try {
      if (!map.getSource(SATELLITE_SOURCE_ID)) {
        map.addSource(SATELLITE_SOURCE_ID, {
          type: "raster",
          tiles: SATELLITE_TILE_URLS,
          tileSize: 256,
        });
      }
      if (!map.getLayer(SATELLITE_LAYER_ID)) {
        map.addLayer(
          {
            id: SATELLITE_LAYER_ID,
            type: "raster",
            source: SATELLITE_SOURCE_ID,
            paint: { "raster-opacity": mapOpacity },
            layout: { visibility: "none" },
          },
          "osm-tiles",
        );
      }
      const showSatellite = (!preview && is3D) || basemap === "satellite";
      map.setLayoutProperty(SATELLITE_LAYER_ID, "visibility", showSatellite ? "visible" : "none");
      map.setLayoutProperty("osm-tiles", "visibility", showSatellite ? "none" : "visible");
    } catch (e) {
      // Style may not be loaded during HMR — ignore
    }
  }, [basemap, is3D, mapOpacity, preview, mapReady, styleReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady) return;

    try {
      if (map.getLayer("osm-tiles")) {
        map.setPaintProperty("osm-tiles", "raster-opacity", mapOpacity);
      }
      if (map.getLayer(SATELLITE_LAYER_ID)) {
        map.setPaintProperty(SATELLITE_LAYER_ID, "raster-opacity", mapOpacity);
      }
    } catch (e) {
      // Style may not be loaded during HMR — ignore
    }
  }, [mapOpacity, mapReady, styleReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady) return;

    try {
      Object.entries(layersData).forEach(([layerName, data]) => {
        const sourceId = `layer-${layerName}`;
        const layerId = `layer-${layerName}-fill`;
        const layerConfig = config.base_layers[layerName];
        if (!layerConfig) return;
        const initialVisibility = visibility[layerName] !== false ? "visible" : "none";

        const source = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
        if (source) {
          source.setData(data);
        } else {
          map.addSource(sourceId, { type: "geojson", data });
        }

        if (map.getLayer(layerId)) return;
        const isLine = layerName === "waterways";
        if (isLine) {
          map.addLayer({
            id: layerId,
            type: "line",
            source: sourceId,
            paint: {
              "line-color": layerConfig.color,
              "line-width": 4,
              "line-opacity": layerConfig.opacity,
            },
            layout: { visibility: initialVisibility },
          });

          // Waterway name labels (AWGN GEW_NAME), placed along the line. Skips
          // "NN-*" placeholder names and only appears once zoomed in enough to be
          // legible; MapLibre's collision handling thins overlapping labels.
          // Shares the waterways layer's own visibility toggle — no separate
          // control, so it never shows up as an extra legend/layer-panel entry.
          const labelsVisible = initialVisibility;
          map.addLayer({
            id: `${layerId}-labels`,
            type: "symbol",
            source: sourceId,
            minzoom: 12,
            filter: [
              "all",
              ["has", "GEW_NAME"],
              ["!=", ["slice", ["to-string", ["get", "GEW_NAME"]], 0, 2], "NN"],
            ] as maplibregl.FilterSpecification,
            layout: {
              "symbol-placement": "line",
              "text-field": ["get", "GEW_NAME"],
              "text-font": ["OpenSans"],
              "text-size": ["interpolate", ["linear"], ["zoom"], 12, 10, 16, 13],
              "symbol-spacing": 350,
              "text-max-angle": 30,
              "text-padding": 4,
              visibility: labelsVisible,
            },
            paint: {
              "text-color": "#0F5E78",
              "text-halo-color": "#ffffff",
              "text-halo-width": 1.6,
              "text-halo-blur": 0.4,
            },
          });
          return;
        }
        map.addLayer({
          id: layerId,
          type: "fill",
          source: sourceId,
          paint: {
            "fill-color": layerConfig.color,
            "fill-opacity": layerConfig.opacity,
          },
          layout: { visibility: initialVisibility },
        });
        map.addLayer({
          id: `${layerId}-outline`,
          type: "line",
          source: sourceId,
          paint: {
            "line-color": layerConfig.color,
            "line-width": 2.25,
            "line-opacity": 0.9,
          },
          layout: { visibility: initialVisibility },
        });
      });
    } catch (e) {
      // Style may not be loaded during HMR — ignore
    }
  }, [config.base_layers, layersData, mapReady, styleReady, visibility]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady || !speciesData) return;

    try {
    const sourceId = "species";
    const taggedSpeciesData = withFocusTags(speciesData, focusData);
    const source = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
    if (source) {
      source.setData(taggedSpeciesData);
      return;
    }

    map.addSource(sourceId, { type: "geojson", data: taggedSpeciesData });
    const speciesVisible =
      mode === "habitats" && visibility.species !== false ? "visible" : "none";
    config.species.forEach((species) => {
      map.addLayer({
        id: `species-${species.id}`,
        type: "line",
        source: sourceId,
        filter: ["==", ["get", "species_id"], species.id],
        paint: {
          "line-color": species.color,
          "line-width": 6,
          "line-opacity": FOCUS_LINE_OPACITY_EXPRESSION,
        },
        layout: {
          "line-cap": "round",
          "line-join": "round",
          visibility: speciesVisible,
        },
      });
    });
    // "Both species" overlap lines draw on top of the per-species lines so the
    // coincident stretches read as green.
    map.addLayer({
      id: "species-both",
      type: "line",
      source: sourceId,
      filter: ["==", ["get", "species_id"], "both"],
      paint: {
        "line-color": config.overlap_colors.both,
        "line-width": 6,
        "line-opacity": FOCUS_LINE_OPACITY_EXPRESSION,
      },
      layout: {
        "line-cap": "round",
        "line-join": "round",
        visibility: speciesVisible,
      },
    });
    } catch (e) {
      // Style may not be loaded during HMR — ignore
    }
  }, [config.overlap_colors.both, config.species, focusData, mapReady, mode, speciesData, styleReady, visibility.species]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady || !measuresData) return;

    const measureVisible =
      mode === "planning" && visibility.measures !== false ? "visible" : "none";

    // Dev-only sanity check: warn (don't silently drop) if a line carries more
    // active measures than we have slots to draw. Extra measures still appear in
    // the details panel; only their dedicated offset line is omitted.
    if (import.meta.env.DEV) {
      const overCap = measuresData.features.filter((f) => {
        const m = f.properties.measures;
        return Array.isArray(m) && m.length > MAX_VISIBLE_MEASURE_SLOTS;
      });
      if (overCap.length > 0) {
        console.warn(
          `[map] ${overCap.length} feature(s) have more than ${MAX_VISIBLE_MEASURE_SLOTS} ` +
            `active measures; extra measures are only shown in the details panel.`,
        );
      }
    }

    try {
      const sourceId = "measures";
      const layerId = "measures-line";
      const taggedMeasuresData = withFocusTags(measuresData, focusData);
      const source = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
      if (source) {
        source.setData(taggedMeasuresData);
      } else {
        // generateId assigns each feature a stable numeric id so the hover
        // affordance below can drive per-feature state (setFeatureState).
        map.addSource(sourceId, { type: "geojson", data: taggedMeasuresData, generateId: true });
      }

      // Base line: an INVISIBLE hit target only. All visible colouring is done by
      // the offset slot layers; a grey line underneath would add clutter with no
      // matching legend entry, so it stays fully transparent. Opacity-0 line
      // layers still register clicks, so this remains the single click target for
      // opening measure details — widened well past its old 3px so taps landing
      // near the (offset) coloured lines still hit it.
      //
      // Exclude synthetic "both species" overlap lines: they carry an empty
      // `measures` list (they only exist to paint the green overlap in the
      // zoomed-out overview), so a click on one would open an empty details
      // drawer and, worse, shadow the real per-species measure lines running
      // along the same reach. Filtering them out here lets those clicks fall
      // through to the actual measures.
      if (!map.getLayer(layerId)) {
        map.addLayer({
          id: layerId,
          type: "line",
          source: sourceId,
          filter: ["!=", ["get", "species_id"], "both"],
          paint: {
            "line-color": "#9E9E9E",
            "line-width": ["interpolate", ["linear"], ["zoom"], 10, 8, 16, 18],
            "line-opacity": 0,
          },
          layout: {
            "line-cap": "round",
            "line-join": "round",
            visibility: measureVisible,
          },
        });

        // Preview instances are non-interactive — no measure clicks or hover
        // cursor, they only exist to frame a printable extent.
        if (!preview) {
          map.on("click", layerId, (event) => {
            if (selectionModeRef.current === "polygon" || measurementActiveRef.current) return;
            const feature = event.features?.[0];
            if (!feature) return;
            onMeasureClickRef.current(
              feature as unknown as GeoJSONFeatureCollection["features"][number],
            );
          });
          map.on("mouseenter", layerId, () => {
            if (measurementActiveRef.current) {
              map.getCanvas().style.cursor = "crosshair";
              return;
            }
            map.getCanvas().style.cursor = "pointer";
          });

          // Hover affordance: mark the hovered feature so the visible tier-1/2
          // lines brighten + thicken (via feature-state in their paint), and
          // float a "click for details" tooltip that follows the cursor. Skipped
          // while the measurement tool owns the pointer.
          const clearMeasureHover = () => {
            if (hoveredMeasureFeatureIdRef.current !== null) {
              map.setFeatureState(
                { source: sourceId, id: hoveredMeasureFeatureIdRef.current },
                { hover: false },
              );
              hoveredMeasureFeatureIdRef.current = null;
            }
            measureTooltipRef.current?.remove();
          };

          map.on("mousemove", layerId, (event) => {
            if (selectionModeRef.current === "polygon" || measurementActiveRef.current) {
              clearMeasureHover();
              return;
            }
            const feature = event.features?.[0];
            if (feature?.id === undefined || feature.id === null) return;

            if (hoveredMeasureFeatureIdRef.current !== feature.id) {
              if (hoveredMeasureFeatureIdRef.current !== null) {
                map.setFeatureState(
                  { source: sourceId, id: hoveredMeasureFeatureIdRef.current },
                  { hover: false },
                );
              }
              hoveredMeasureFeatureIdRef.current = feature.id;
              map.setFeatureState({ source: sourceId, id: feature.id }, { hover: true });
            }

            if (!measureTooltipRef.current) {
              measureTooltipRef.current = new maplibregl.Popup({
                closeButton: false,
                closeOnClick: false,
                offset: 14,
                className: "flima-measure-tooltip",
              });
            }
            measureTooltipRef.current
              .setLngLat(event.lngLat)
              .setText(measureTooltipTextRef.current)
              .addTo(map);
          });

          map.on("mouseleave", layerId, () => {
            map.getCanvas().style.cursor = measurementActiveRef.current ? "crosshair" : "";
            clearMeasureHover();
          });
        }
      }

      // === Tier 1 (zoom < overviewMax): normally one line per species-owned
      // habitat, coloured by species. If every species is deselected, fall back
      // to standalone measures so the overview is not blank.
      // Hidden at/above the breakpoint via `maxzoom` — a hard handoff to Tier 2,
      // so the two tiers can never both be invisible at the same zoom. ===
      const overviewLayerId = "measure-overview";
      const inFocus = ["boolean", ["get", "in_focus"], true];
      // Overview shows every line in the (already species/month-filtered)
      // source. With species selected that's species-owned habitat + the "both"
      // overlap AND standalone measures (species_id === "", e.g. krebspest): the
      // old `species_id != ""` test hid standalone lines below the breakpoint,
      // so they vanished when zooming out. With no species selected the source
      // holds only standalone lines, so match those.
      const overviewFilter = (
        selectedSpeciesCount === 0
          ? ["==", ["get", "species_id"], ""]
          : ["any", ["!=", ["get", "species_id"], ""], ["==", ["get", "species_id"], ""]]
      ) as maplibregl.FilterSpecification;
      const speciesColorExpression = [
        "case",
        ["has", "species_color"],
        ["get", "species_color"],
        [
          "match",
          ["get", "species_id"],
          ...config.species.flatMap((s) => [s.id, s.color]),
          "#808080",
        ],
      ] as unknown as maplibregl.ExpressionSpecification;
      // On hover: thicken ~1.5× and go fully opaque, without changing colour.
      // `zoom` must stay the top-level interpolate input, so the hover
      // multiplier is folded into each stop's output instead of wrapping the
      // whole expression in a multiply (which MapLibre rejects).
      const hover = ["boolean", ["feature-state", "hover"], false];
      const overviewWidthExpr = [
        "interpolate", ["linear"], ["zoom"],
        8, ["case", hover, 7.5, 5],
        12.99, ["case", hover, 9, 6],
      ];
      const overviewOpacityExpr = [
        "case",
        hover,
        1,
        inFocus,
        FOCUS_LINE_OPACITY_INSIDE,
        FOCUS_LINE_OPACITY_OUTSIDE,
      ];

      if (!map.getLayer(overviewLayerId)) {
        map.addLayer({
          id: overviewLayerId,
          type: "line",
          source: sourceId,
          filter: overviewFilter,
          maxzoom: ZOOM_BREAKPOINTS.overviewMax,
          paint: {
            "line-color": speciesColorExpression,
            "line-width": overviewWidthExpr as maplibregl.ExpressionSpecification,
            "line-opacity": overviewOpacityExpr as maplibregl.ExpressionSpecification,
          },
          layout: {
            "line-cap": "round",
            "line-join": "round",
            visibility: measureVisible,
          },
        });
      } else {
        map.setFilter(overviewLayerId, overviewFilter);
        map.setPaintProperty(overviewLayerId, "line-color", speciesColorExpression);
        map.setPaintProperty(overviewLayerId, "line-width", overviewWidthExpr);
        map.setPaintProperty(overviewLayerId, "line-opacity", overviewOpacityExpr);
      }

      // === Tier 2 (zoom >= overviewMax): per-measure colour via parallel offset
      // "slots". Slot N draws the Nth active measure of each line as its own thin
      // line, offset perpendicular so 2–4 concurrent measures stay legible instead
      // of occluding each other (old stacked lines) or clumping (old dots). ===
      const measuresArray: maplibregl.ExpressionSpecification = [
        "coalesce",
        ["get", "measures"],
        ["literal", []],
      ] as maplibregl.ExpressionSpecification;
      const measureColorPairs = config.measures.flatMap((m) => [m.id, m.color]);

      for (let slot = 0; slot < MAX_VISIBLE_MEASURE_SLOTS; slot++) {
        const slotLayerId = `measure-slot-${slot}`;
        const slotColorExpr = [
          "match",
          ["to-string", ["at", slot, measuresArray]],
          ...measureColorPairs,
          "#808080",
        ] as maplibregl.ExpressionSpecification;

        const slotFilter = [">", ["length", measuresArray], slot] as maplibregl.FilterSpecification;
        // Centre the line's slots around its true geometry:
        //   offset = spacing * (slot - (count - 1) / 2)
        const slotOffsetExpr = [
          "*",
          SLOT_SPACING_PX,
          ["-", slot, ["/", ["-", ["length", measuresArray], 1], 2]],
        ];
        // On hover: thicken ~1.6× and go fully opaque. All slots of the hovered
        // feature share its feature-state, so they brighten together. The hover
        // multiplier is folded into each zoom stop's output (see overview note).
        const slotHover = ["boolean", ["feature-state", "hover"], false];
        // Widths anchored at the overview→detail breakpoint (zoom 12). The old
        // ramp started at zoom 13 / 2.5px, so 12–12.9 clamped to a hairline that
        // read as "measures disappeared" beside the bold 6px overview lines.
        // Start thicker at the handoff; slots stay < SLOT_SPACING_PX apart so
        // neighbouring offset lines don't merge.
        const slotWidthExpr = [
          "interpolate", ["linear"], ["zoom"],
          12, ["case", slotHover, 5, 3.5],
          16, ["case", slotHover, 5.8, 4],
          18, ["case", slotHover, 6.6, 4.5],
        ];
        const slotOpacityExpr = [
          "case",
          slotHover,
          1,
          inFocus,
          0.9,
          FOCUS_LINE_OPACITY_OUTSIDE,
        ];

        if (map.getLayer(slotLayerId)) {
          map.setFilter(slotLayerId, slotFilter);
          map.setPaintProperty(slotLayerId, "line-color", slotColorExpr);
          map.setPaintProperty(slotLayerId, "line-offset", slotOffsetExpr);
          map.setPaintProperty(slotLayerId, "line-width", slotWidthExpr);
          map.setPaintProperty(slotLayerId, "line-opacity", slotOpacityExpr);
          continue;
        }

        map.addLayer({
          id: slotLayerId,
          type: "line",
          source: sourceId,
          minzoom: ZOOM_BREAKPOINTS.overviewMax,
          // Only lines that actually have an Nth measure participate in slot N.
          filter: slotFilter,
          paint: {
            "line-color": slotColorExpr,
            "line-offset": slotOffsetExpr as maplibregl.ExpressionSpecification,
            "line-width": slotWidthExpr as maplibregl.ExpressionSpecification,
            "line-opacity": slotOpacityExpr as maplibregl.ExpressionSpecification,
          },
          layout: {
            "line-cap": "round",
            "line-join": "round",
            visibility: measureVisible,
          },
        });
      }
    } catch (e) {
      console.error("Map measures layer error:", e);
    }
  }, [config.measures, config.species, focusData, mapReady, measuresData, mode, preview, selectedSpeciesCount, styleReady, visibility.measures]);

  // "No work permitted" — habitat lines with no measure allowed under the
  // current filter, drawn as a red dashed line at every zoom. Shares the
  // measures layer's visibility (planning mode + measures toggle).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady) return;

    const sourceId = "no-work";
    const layerId = "no-work-line";
    const data: GeoJSONFeatureCollection = noWorkData
      ? withFocusTags(noWorkData, focusData)
      : { type: "FeatureCollection", features: [] };

    try {
      const source = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
      if (source) {
        source.setData(data);
      } else {
        map.addSource(sourceId, { type: "geojson", data });
      }

      if (!map.getLayer(layerId)) {
        map.addLayer({
          id: layerId,
          type: "line",
          source: sourceId,
          paint: {
            "line-color": "#DC2626",
            "line-width": ["interpolate", ["linear"], ["zoom"], 10, 3.5, 16, 6],
            "line-dasharray": [2, 1.6],
            "line-opacity": FOCUS_LINE_OPACITY_EXPRESSION,
          },
          layout: {
            "line-cap": "butt",
            "line-join": "round",
            visibility:
              mode === "planning" && visibility.measures !== false ? "visible" : "none",
          },
        });
      } else {
        map.setPaintProperty(layerId, "line-opacity", FOCUS_LINE_OPACITY_EXPRESSION);
      }
    } catch (e) {
      console.error("Map no-work layer error:", e);
    }
  }, [focusData, mapReady, noWorkData, mode, styleReady, visibility.measures]);

  // Flash highlight effect when a measure is clicked in the legend
  // Highlights ALL lines that have the selected measure active
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady) return;

    const sourceId = "highlight";
    const layerId = "highlight-line";

    // Clean up previous highlight
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    } catch {
      // Layer/source may already be removed
    }

    if (!highlightMeasureId || !measuresData) return;

    // Find the measure's colour from config
    const measureCfg = config.measures.find((m) => m.id === highlightMeasureId);
    const highlightColor = measureCfg?.color || "#FFFFFF";

    // Filter to only features that have the selected measure active
    const highlightedFeatures = measuresData.features.filter((f) => {
      const active = Array.isArray(f.properties.measures)
        ? (f.properties.measures as string[])
        : [];
      return active.includes(highlightMeasureId);
    });

    if (highlightedFeatures.length === 0) return;

    const featureCollection = {
      type: "FeatureCollection" as const,
      features: highlightedFeatures,
    };

    try {
      map.addSource(sourceId, { type: "geojson", data: featureCollection });
      map.addLayer({
        id: layerId,
        type: "line",
        source: sourceId,
        paint: {
          "line-color": highlightColor,
          "line-width": 10,
          "line-opacity": 0.9,
          "line-blur": 2,
        },
        layout: { visibility: "visible" },
      });

      // Zoom to fit all highlighted features
      const highlightBounds = getDataBounds(featureCollection);
      if (highlightBounds) {
        map.fitBounds(highlightBounds, {
          padding: 60,
          maxZoom: 16,
          duration: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 800,
        });
      }

      // Flash animation: pulse the line width and opacity
      let frame = 0;
      const flashInterval = setInterval(() => {
        if (!map.getLayer(layerId)) {
          clearInterval(flashInterval);
          return;
        }
        frame++;
        const phase = Math.sin(frame * 0.3);
        const width = 8 + phase * 5;
        const opacity = 0.4 + Math.abs(phase) * 0.6;
        map.setPaintProperty(layerId, "line-width", width);
        map.setPaintProperty(layerId, "line-opacity", opacity);
      }, 100);

      return () => {
        clearInterval(flashInterval);
        try {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
          if (map.getSource(sourceId)) map.removeSource(sourceId);
        } catch {
          // Already removed
        }
      };
    } catch {
      // Map not ready or layer already exists
    }
  }, [config.measures, highlightMeasureId, mapReady, measuresData, styleReady]);

  // Line overlay — draws a solid coloured line for one measure when toggled
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const sourceId = "line-overlay";
    const layerId = "line-overlay-line";

    // Clean up previous overlay
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    } catch {
      // Already removed
    }

    if (!lineOverlayMeasureId || !measuresData) return;

    const measureCfg = config.measures.find((m) => m.id === lineOverlayMeasureId);
    if (!measureCfg) return;

    // Filter to features that have this measure active
    const overlayFeatures = measuresData.features.filter((f) => {
      const active = Array.isArray(f.properties.measures)
        ? (f.properties.measures as string[])
        : [];
      return active.includes(lineOverlayMeasureId);
    });

    if (overlayFeatures.length === 0) return;

    const featureCollection = {
      type: "FeatureCollection" as const,
      features: overlayFeatures,
    };
    const taggedFeatureCollection = withFocusTags(featureCollection, focusData);

    try {
      map.addSource(sourceId, { type: "geojson", data: taggedFeatureCollection });
      map.addLayer({
        id: layerId,
        type: "line",
        source: sourceId,
        paint: {
          "line-color": measureCfg.color,
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8, 3,
            12, 5,
            16, 7,
          ],
          "line-opacity": [
            "case",
            ["boolean", ["get", "in_focus"], true],
            0.85,
            FOCUS_LINE_OPACITY_OUTSIDE,
          ] as maplibregl.ExpressionSpecification,
        },
        layout: { visibility: "visible" },
      });
    } catch {
      // Style not loaded
    }
  }, [lineOverlayMeasureId, focusData, mapReady, measuresData, config.measures, styleReady]);

  // No-work flash highlight — pulses all "no work permitted" lines red when
  // the no-work legend entry is clicked. Mirrors the measure highlight effect
  // but operates on noWorkData instead of measuresData.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady) return;

    const sourceId = "no-work-highlight";
    const layerId = "no-work-highlight-line";

    // Clean up previous highlight
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    } catch {
      // Layer/source may already be removed
    }

    if (!highlightNoWork || !noWorkData || noWorkData.features.length === 0) return;

    try {
      map.addSource(sourceId, { type: "geojson", data: noWorkData });
      map.addLayer({
        id: layerId,
        type: "line",
        source: sourceId,
        paint: {
          "line-color": "#DC2626",
          "line-width": 10,
          "line-opacity": 0.9,
          "line-blur": 2,
        },
        layout: { visibility: "visible" },
      });

      // Zoom to fit all no-work features
      const highlightBounds = getDataBounds(noWorkData);
      if (highlightBounds) {
        map.fitBounds(highlightBounds, {
          padding: 60,
          maxZoom: 16,
          duration: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 800,
        });
      }

      // Flash animation: pulse the line width and opacity
      let frame = 0;
      const flashInterval = setInterval(() => {
        if (!map.getLayer(layerId)) {
          clearInterval(flashInterval);
          return;
        }
        frame++;
        const phase = Math.sin(frame * 0.3);
        const width = 8 + phase * 5;
        const opacity = 0.4 + Math.abs(phase) * 0.6;
        map.setPaintProperty(layerId, "line-width", width);
        map.setPaintProperty(layerId, "line-opacity", opacity);
      }, 100);

      return () => {
        clearInterval(flashInterval);
        try {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
          if (map.getSource(sourceId)) map.removeSource(sourceId);
        } catch {
          // Already removed
        }
      };
    } catch {
      // Map not ready or layer already exists
    }
  }, [highlightNoWork, mapReady, noWorkData, styleReady]);

  // No-work line overlay — draws a solid red line for all no-work areas when
  // the no-work legend overlay toggle is pressed. Mirrors the measure overlay.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady) return;

    const sourceId = "no-work-overlay";
    const layerId = "no-work-overlay-line";

    // Clean up previous overlay
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    } catch {
      // Already removed
    }

    if (!lineOverlayNoWork || !noWorkData || noWorkData.features.length === 0) return;
    const taggedNoWorkData = withFocusTags(noWorkData, focusData);

    try {
      map.addSource(sourceId, { type: "geojson", data: taggedNoWorkData });
      map.addLayer({
        id: layerId,
        type: "line",
        source: sourceId,
        paint: {
          "line-color": "#DC2626",
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8, 3,
            12, 5,
            16, 7,
          ],
          "line-opacity": [
            "case",
            ["boolean", ["get", "in_focus"], true],
            0.85,
            FOCUS_LINE_OPACITY_OUTSIDE,
          ] as maplibregl.ExpressionSpecification,
        },
        layout: { visibility: "visible" },
      });
    } catch {
      // Style not loaded
    }
  }, [focusData, lineOverlayNoWork, mapReady, noWorkData, styleReady]);

  // Polygon/viewport selection drawing + click handler
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady) return;

    const sourceId = "selection-polygon";
    const fillLayerId = "selection-polygon-fill";
    const outlineLayerId = "selection-polygon-outline";
    const verticesLayerId = "selection-polygon-vertices";

    // Build the selection geometry
    let selectionData: GeoJSONFeatureCollection = { type: "FeatureCollection", features: [] };

    if (selectionMode === "viewport" && viewportPolygon) {
      selectionData = {
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          geometry: { type: "Polygon", coordinates: [viewportPolygon] },
          properties: {},
        }],
      };
    } else if (selectionMode === "polygon") {
      const features: GeoJSONFeatureCollection["features"] = [];

      if (polygonClosed && polygonPoints.length >= 3) {
        // Closed polygon
        const ring = [...polygonPoints.map((p) => [p[0], p[1]]), [polygonPoints[0][0], polygonPoints[0][1]]];
        features.push({
          type: "Feature",
          geometry: { type: "Polygon", coordinates: [ring] },
          properties: {},
        });
      } else if (polygonPoints.length >= 2) {
        // Open line preview
        features.push({
          type: "Feature",
          geometry: { type: "LineString", coordinates: polygonPoints.map((p) => [p[0], p[1]]) },
          properties: {},
        });
      }

      // Vertex markers
      polygonPoints.forEach((pt) => {
        features.push({
          type: "Feature",
          geometry: { type: "Point", coordinates: [pt[0], pt[1]] },
          properties: {},
        });
      });

      selectionData = { type: "FeatureCollection", features };
    }

    // Update source
    try {
      const source = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
      if (source) {
        source.setData(selectionData);
      } else {
        map.addSource(sourceId, { type: "geojson", data: selectionData });
        map.addLayer({
          id: fillLayerId,
          type: "fill",
          source: sourceId,
          paint: { "fill-color": "#1A6B5A", "fill-opacity": 0.12 },
          filter: ["==", ["geometry-type"], "Polygon"],
          layout: { visibility: "visible" },
        });
        map.addLayer({
          id: outlineLayerId,
          type: "line",
          source: sourceId,
          paint: {
            "line-color": "#1A6B5A",
            "line-width": 2,
            "line-dasharray": [2, 1],
            "line-opacity": 0.8,
          },
          filter: ["any",
            ["==", ["geometry-type"], "Polygon"],
            ["==", ["geometry-type"], "LineString"],
          ],
          layout: { visibility: "visible" },
        });
        map.addLayer({
          id: verticesLayerId,
          type: "circle",
          source: sourceId,
          paint: {
            "circle-radius": 7,
            "circle-color": "#1A6B5A",
            "circle-stroke-color": "#FFFFFF",
            "circle-stroke-width": 2.5,
          },
          filter: ["==", ["geometry-type"], "Point"],
          layout: { visibility: "visible" },
        });
      }
    } catch {
      // Style not loaded
    }

    // Click handler for polygon vertex placement
    const handleClick = (e: maplibregl.MapMouseEvent) => {
      if (selectionModeRef.current !== "polygon") return;
      onPolygonPointAddRef.current(e.lngLat.lng, e.lngLat.lat);
    };

    // Double-click finishes the polygon
    const handleDblClick = (e: maplibregl.MapMouseEvent) => {
      if (selectionModeRef.current !== "polygon") return;
      e.preventDefault();
      onPolygonFinishRef.current();
    };

    if (selectionMode === "polygon") {
      map.on("click", handleClick);
      map.on("dblclick", handleDblClick);
      map.getCanvas().style.cursor = "crosshair";
    } else {
      map.getCanvas().style.cursor = "";
    }

    return () => {
      map.off("click", handleClick);
      map.off("dblclick", handleDblClick);
      if (selectionMode !== "polygon") {
        map.getCanvas().style.cursor = "";
      }
    };
  }, [selectionMode, polygonPoints, polygonClosed, viewportPolygon, mapReady, styleReady]);

  // Keyboard shortcuts for polygon selection
  useEffect(() => {
    if (selectionMode !== "polygon") return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onSelectionClearRef.current();
      } else if (e.key === "Backspace") {
        e.preventDefault();
        onPolygonUndoRef.current();
      } else if (e.key === "Enter") {
        onPolygonFinishRef.current();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [selectionMode]);

  useEffect(() => {
    const map = mapRef.current;
    if (preview || !map || !mapReady || !styleReady) return;

    const data = measurementGeoJson(measurementPoints, measurementHover, measurementActive);
    try {
      const source = map.getSource(MEASUREMENT_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      if (source) {
        source.setData(data);
      } else {
        map.addSource(MEASUREMENT_SOURCE_ID, { type: "geojson", data });
      }

      if (!map.getLayer(MEASUREMENT_LINE_CASING_LAYER_ID)) {
        map.addLayer({
          id: MEASUREMENT_LINE_CASING_LAYER_ID,
          type: "line",
          source: MEASUREMENT_SOURCE_ID,
          filter: ["==", ["get", "kind"], "line"],
          paint: {
            "line-color": "#FFFFFF",
            "line-width": 8,
            "line-opacity": 0.92,
          },
          layout: {
            "line-cap": "round",
            "line-join": "round",
            visibility: "visible",
          },
        });
      }

      if (!map.getLayer(MEASUREMENT_LINE_LAYER_ID)) {
        map.addLayer({
          id: MEASUREMENT_LINE_LAYER_ID,
          type: "line",
          source: MEASUREMENT_SOURCE_ID,
          filter: ["==", ["get", "kind"], "line"],
          paint: {
            "line-color": "#147D92",
            "line-width": 4,
            "line-opacity": 0.96,
          },
          layout: {
            "line-cap": "round",
            "line-join": "round",
            visibility: "visible",
          },
        });
      }

      if (!map.getLayer(MEASUREMENT_PREVIEW_LAYER_ID)) {
        map.addLayer({
          id: MEASUREMENT_PREVIEW_LAYER_ID,
          type: "line",
          source: MEASUREMENT_SOURCE_ID,
          filter: ["==", ["get", "kind"], "preview"],
          paint: {
            "line-color": "#147D92",
            "line-width": 3,
            "line-dasharray": [1.2, 1],
            "line-opacity": 0.72,
          },
          layout: {
            "line-cap": "round",
            "line-join": "round",
            visibility: "visible",
          },
        });
      }

      if (!map.getLayer(MEASUREMENT_VERTEX_LAYER_ID)) {
        map.addLayer({
          id: MEASUREMENT_VERTEX_LAYER_ID,
          type: "circle",
          source: MEASUREMENT_SOURCE_ID,
          filter: ["==", ["get", "kind"], "vertex"],
          paint: {
            "circle-radius": 6.5,
            "circle-color": "#147D92",
            "circle-stroke-color": "#FFFFFF",
            "circle-stroke-width": 2.5,
          },
          layout: { visibility: "visible" },
        });
      }
      raiseMeasurementLayers(map);
    } catch {
      // Style may not be loaded during HMR — ignore
    }
  }, [mapReady, measurementActive, measurementHover, measurementPoints, preview, styleReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (preview || !measurementActive || !map || !mapReady) return;

    const canvas = map.getCanvas();
    const previousCursor = canvas.style.cursor;
    const handleClick = (event: maplibregl.MapMouseEvent) => {
      event.preventDefault();
      addMeasurementPoint(event.lngLat.lng, event.lngLat.lat);
    };
    const handleMouseMove = (event: maplibregl.MapMouseEvent) => {
      setMeasurementHover([event.lngLat.lng, event.lngLat.lat]);
    };
    const handleCanvasLeave = () => {
      setMeasurementHover(null);
    };

    map.on("click", handleClick);
    map.on("mousemove", handleMouseMove);
    canvas.addEventListener("mouseleave", handleCanvasLeave);
    canvas.style.cursor = "crosshair";

    return () => {
      map.off("click", handleClick);
      map.off("mousemove", handleMouseMove);
      canvas.removeEventListener("mouseleave", handleCanvasLeave);
      if (canvas.style.cursor === "crosshair") canvas.style.cursor = previousCursor;
    };
  }, [addMeasurementPoint, mapReady, measurementActive, preview]);

  useEffect(() => {
    if (preview || !measurementActive) return;
    const handleKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const targetTag = target?.tagName;
      const isControlTarget =
        target?.isContentEditable ||
        targetTag === "BUTTON" ||
        targetTag === "INPUT" ||
        targetTag === "SELECT" ||
        targetTag === "TEXTAREA";

      if (event.key === "Escape") {
        event.preventDefault();
        if (measurementPointsRef.current.length < 2) clearMeasurement();
        else finishMeasurement();
        return;
      }
      if (isControlTarget) return;
      if (event.key === "Backspace") {
        event.preventDefault();
        undoMeasurementPoint();
        return;
      }
      if (event.key !== "Enter" && event.key !== " ") return;

      const map = mapRef.current;
      if (!map) return;
      const center = map.getCenter();
      event.preventDefault();
      addMeasurementPoint(center.lng, center.lat);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [
    addMeasurementPoint,
    clearMeasurement,
    finishMeasurement,
    measurementActive,
    preview,
    undoMeasurementPoint,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady || !focusData) return;

    try {
    const sourceId = "focus-municipality";
    const fillLayerId = "focus-municipality-fill";
    const outlineLayerId = "focus-municipality-outline";
    const source = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
    if (source) source.setData(focusData);
    else map.addSource(sourceId, { type: "geojson", data: focusData });

    if (!map.getLayer(fillLayerId)) {
      map.addLayer({
        id: fillLayerId,
        type: "fill",
        source: sourceId,
        paint: {
          "fill-color": "#173F35",
          "fill-opacity": 0.07,
        },
      });
    }
    if (!map.getLayer(outlineLayerId)) {
      map.addLayer({
        id: outlineLayerId,
        type: "line",
        source: sourceId,
        paint: {
          "line-color": FOCUS_OUTLINE_COLOR_2D,
          "line-width": 3.5,
          "line-opacity": 0.95,
          "line-dasharray": [2, 1],
        },
      });
    }
    // Dark green disappears against satellite imagery — switch to white
    // whenever a satellite basemap is showing (live 3D, live satellite toggle,
    // or preview satellite). Set unconditionally (not just on creation) so
    // switching basemap recolours an outline added beforehand.
    const darkBasemap = is3D || basemap === "satellite";
    map.setPaintProperty(
      outlineLayerId,
      "line-color",
      darkBasemap ? FOCUS_OUTLINE_COLOR_3D : FOCUS_OUTLINE_COLOR_2D,
    );

    const bounds = getDataBounds(focusData);
    if (!bounds) return;
    const focusKey = bounds.toArray().flat().join(":");
    if (fittedFocusRef.current === focusKey) return;
    fittedFocusRef.current = focusKey;
    const fitPadding = Math.max(
      20,
      Math.min(48, Math.round(Math.min(window.innerWidth, window.innerHeight) * 0.06)),
    );
    map.fitBounds(bounds, {
      padding: fitPadding,
      maxZoom: 14,
      duration: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 650,
    });
    } catch (e) {
      // Style may not be loaded during HMR — ignore
    }
  }, [basemap, focusData, is3D, mapReady, preview, styleReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (preview || !map || !mapReady || !styleReady) return;

    const data = municipalityPreviewData ?? EMPTY_FEATURE_COLLECTION;
    try {
      const source = map.getSource(MUNICIPALITY_PREVIEW_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      if (source) {
        source.setData(data);
      } else {
        map.addSource(MUNICIPALITY_PREVIEW_SOURCE_ID, { type: "geojson", data });
      }

      if (!map.getLayer(MUNICIPALITY_PREVIEW_FILL_LAYER_ID)) {
        map.addLayer({
          id: MUNICIPALITY_PREVIEW_FILL_LAYER_ID,
          type: "fill",
          source: MUNICIPALITY_PREVIEW_SOURCE_ID,
          paint: {
            "fill-color": "#1A6B5A",
            "fill-opacity": 0.1,
          },
        });
      }

      if (!map.getLayer(MUNICIPALITY_PREVIEW_OUTLINE_LAYER_ID)) {
        map.addLayer({
          id: MUNICIPALITY_PREVIEW_OUTLINE_LAYER_ID,
          type: "line",
          source: MUNICIPALITY_PREVIEW_SOURCE_ID,
          paint: {
            "line-color": "#1A6B5A",
            "line-width": 3,
            "line-opacity": 0.9,
            "line-dasharray": [1.5, 0.8],
          },
        });
      }
      raiseMunicipalityLayers(map);
      raiseMeasurementLayers(map);
    } catch {
      // Style may not be loaded during HMR — ignore
    }
  }, [mapReady, municipalityPreviewData, preview, styleReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (preview || !map || !mapReady || !styleReady) return;

    const data = municipalityLocations ?? EMPTY_FEATURE_COLLECTION;
    try {
      if (!map.hasImage(MUNICIPALITY_MARKER_IMAGE_ID)) {
        map.addImage(MUNICIPALITY_MARKER_IMAGE_ID, createMunicipalityMarkerImage(), {
          pixelRatio: 2,
        });
      }

      const source = map.getSource(MUNICIPALITY_MARKERS_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      if (source) {
        source.setData(data);
      } else {
        map.addSource(MUNICIPALITY_MARKERS_SOURCE_ID, {
          type: "geojson",
          data,
          cluster: true,
          clusterMaxZoom: 9,
          clusterRadius: 46,
        });
      }

      if (!map.getLayer(MUNICIPALITY_CLUSTER_LAYER_ID)) {
        map.addLayer({
          id: MUNICIPALITY_CLUSTER_LAYER_ID,
          type: "circle",
          source: MUNICIPALITY_MARKERS_SOURCE_ID,
          filter: ["has", "point_count"],
          paint: {
            "circle-color": "#1A6B5A",
            "circle-radius": [
              "step",
              ["get", "point_count"],
              15,
              10,
              18,
              25,
              22,
            ],
            "circle-opacity": 0.82,
            "circle-stroke-color": "#FFFFFF",
            "circle-stroke-width": 2,
          },
        });
      }

      if (!map.getLayer(MUNICIPALITY_MARKER_LAYER_ID)) {
        map.addLayer({
          id: MUNICIPALITY_MARKER_LAYER_ID,
          type: "symbol",
          source: MUNICIPALITY_MARKERS_SOURCE_ID,
          filter: ["!", ["has", "point_count"]],
          layout: {
            "icon-image": MUNICIPALITY_MARKER_IMAGE_ID,
            "icon-anchor": "bottom",
            "icon-allow-overlap": false,
            "icon-size": [
              "interpolate",
              ["linear"],
              ["zoom"],
              9,
              0.9,
              12,
              0.9,
              14,
              0.62,
              16,
              0.5,
            ],
          },
          paint: {
            "icon-opacity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              9,
              0.92,
              12,
              0.88,
              14,
              0.45,
              16,
              0.32,
            ],
          },
        });
      }
      raiseMunicipalityLayers(map);
      raiseMeasurementLayers(map);
    } catch {
      // Style may not be loaded during HMR — ignore
    }

    if (
      !map.getLayer(MUNICIPALITY_CLUSTER_LAYER_ID) ||
      !map.getLayer(MUNICIPALITY_MARKER_LAYER_ID)
    ) {
      return;
    }

    const showPopup = (lngLat: maplibregl.LngLatLike, name: string) => {
      municipalityPopupRef.current?.remove();
      municipalityPopupRef.current = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 16,
      })
        .setLngLat(lngLat)
        .setText(name)
        .addTo(map);
    };

    const clearPreview = () => {
      pendingTouchMunicipalityRef.current = null;
      municipalityPopupRef.current?.remove();
      municipalityPopupRef.current = null;
      onMunicipalityPreviewRef.current?.(null);
    };

    const handleClusterClick = (event: maplibregl.MapLayerMouseEvent) => {
      if (measurementActiveRef.current) return;
      clearPreview();
      map.easeTo({
        center: event.lngLat,
        zoom: Math.min(map.getZoom() + 2, 12),
        duration: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 450,
      });
    };

    const handleMarkerMouseEnter = (event: maplibregl.MapLayerMouseEvent) => {
      if (measurementActiveRef.current) {
        map.getCanvas().style.cursor = "crosshair";
        return;
      }
      if (window.matchMedia("(pointer: coarse)").matches) return;
      const feature = event.features?.[0];
      const uid = municipalityUidFromFeature(feature);
      const name = municipalityNameFromFeature(feature);
      if (!uid || !name) return;
      map.getCanvas().style.cursor = "pointer";
      onMunicipalityPreviewRef.current?.(uid);
      showPopup(event.lngLat, name);
    };

    const handleMarkerMouseLeave = () => {
      if (measurementActiveRef.current) {
        map.getCanvas().style.cursor = "crosshair";
        return;
      }
      if (window.matchMedia("(pointer: coarse)").matches) return;
      map.getCanvas().style.cursor = "";
      clearPreview();
    };

    const handleMarkerClick = (event: maplibregl.MapLayerMouseEvent) => {
      if (measurementActiveRef.current) return;
      event.preventDefault();
      const feature = event.features?.[0];
      const uid = municipalityUidFromFeature(feature);
      const name = municipalityNameFromFeature(feature);
      if (!uid || !name) return;

      if (window.matchMedia("(pointer: coarse)").matches) {
        // Two-tap on touch: first tap previews, second (same marker) selects.
        // Compared by UID so two same-named markers stay distinct.
        if (pendingTouchMunicipalityRef.current === uid) {
          clearPreview();
          onMunicipalitySelectRef.current?.(uid);
          return;
        }
        pendingTouchMunicipalityRef.current = uid;
        onMunicipalityPreviewRef.current?.(uid);
        showPopup(event.lngLat, name);
        return;
      }

      clearPreview();
      onMunicipalitySelectRef.current?.(uid);
    };

    const handleClusterMouseEnter = () => {
      if (measurementActiveRef.current) {
        map.getCanvas().style.cursor = "crosshair";
        return;
      }
      map.getCanvas().style.cursor = "pointer";
    };
    const handleClusterMouseLeave = () => {
      map.getCanvas().style.cursor = measurementActiveRef.current ? "crosshair" : "";
    };
    const handleMapClick = (event: maplibregl.MapMouseEvent) => {
      if (measurementActiveRef.current) return;
      const features = map.queryRenderedFeatures(event.point, {
        layers: [MUNICIPALITY_MARKER_LAYER_ID, MUNICIPALITY_CLUSTER_LAYER_ID],
      });
      if (features.length === 0) clearPreview();
    };

    map.on("click", MUNICIPALITY_CLUSTER_LAYER_ID, handleClusterClick);
    map.on("mouseenter", MUNICIPALITY_CLUSTER_LAYER_ID, handleClusterMouseEnter);
    map.on("mouseleave", MUNICIPALITY_CLUSTER_LAYER_ID, handleClusterMouseLeave);
    map.on("mouseenter", MUNICIPALITY_MARKER_LAYER_ID, handleMarkerMouseEnter);
    map.on("mouseleave", MUNICIPALITY_MARKER_LAYER_ID, handleMarkerMouseLeave);
    map.on("click", MUNICIPALITY_MARKER_LAYER_ID, handleMarkerClick);
    map.on("click", handleMapClick);

    return () => {
      map.off("click", MUNICIPALITY_CLUSTER_LAYER_ID, handleClusterClick);
      map.off("mouseenter", MUNICIPALITY_CLUSTER_LAYER_ID, handleClusterMouseEnter);
      map.off("mouseleave", MUNICIPALITY_CLUSTER_LAYER_ID, handleClusterMouseLeave);
      map.off("mouseenter", MUNICIPALITY_MARKER_LAYER_ID, handleMarkerMouseEnter);
      map.off("mouseleave", MUNICIPALITY_MARKER_LAYER_ID, handleMarkerMouseLeave);
      map.off("click", MUNICIPALITY_MARKER_LAYER_ID, handleMarkerClick);
      map.off("click", handleMapClick);
      pendingTouchMunicipalityRef.current = null;
      municipalityPopupRef.current?.remove();
      municipalityPopupRef.current = null;
    };
  }, [mapReady, municipalityLocations, preview, styleReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (preview || !map || !mapReady || !styleReady) return;
    const frame = window.requestAnimationFrame(() => {
      try {
        raiseMunicipalityLayers(map);
        raiseMeasurementLayers(map);
      } catch {
        // Layers may be in flux while the style is rebuilding.
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    highlightMeasureId,
    highlightNoWork,
    lineOverlayMeasureId,
    lineOverlayNoWork,
    mapReady,
    municipalityLocations,
    municipalityPreviewData,
    preview,
    selectionMode,
    styleReady,
  ]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !styleReady) return;

    try {
    const speciesVis = mode === "habitats" && visibility.species !== false ? "visible" : "none";
    config.species.forEach((species) => {
      const layerId = `species-${species.id}`;
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "visibility", speciesVis);
      }
    });
    if (map.getLayer("species-both")) {
      map.setLayoutProperty("species-both", "visibility", speciesVis);
    }

    if (map.getLayer("measures-line")) {
      map.setLayoutProperty(
        "measures-line",
        "visibility",
        mode === "planning" && visibility.measures !== false ? "visible" : "none",
      );
    }

    // Toggle measure layers at all zoom levels
    const measureVis = mode === "planning" && visibility.measures !== false ? "visible" : "none";

    const overviewLayerId = "measure-overview";
    if (map.getLayer(overviewLayerId)) {
      map.setLayoutProperty(overviewLayerId, "visibility", measureVis);
    }

    // Toggle the per-measure offset "slot" layers (Tier 2).
    for (let slot = 0; slot < MAX_VISIBLE_MEASURE_SLOTS; slot++) {
      const layerId = `measure-slot-${slot}`;
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "visibility", measureVis);
      }
    }

    // "No work permitted" red dashed lines share the measures visibility.
    if (map.getLayer("no-work-line")) {
      map.setLayoutProperty("no-work-line", "visibility", measureVis);
    }

    Object.keys(config.base_layers).forEach((layerName) => {
      const layerId = `layer-${layerName}-fill`;
      if (!map.getLayer(layerId)) return;
      const layerVisibility = visibility[layerName] !== false ? "visible" : "none";
      map.setLayoutProperty(layerId, "visibility", layerVisibility);
      const outlineLayerId = `${layerId}-outline`;
      if (map.getLayer(outlineLayerId)) {
        map.setLayoutProperty(outlineLayerId, "visibility", layerVisibility);
      }
      // Waterway name labels share the parent layer's own toggle.
      const labelsLayerId = `${layerId}-labels`;
      if (map.getLayer(labelsLayerId)) {
        map.setLayoutProperty(labelsLayerId, "visibility", layerVisibility);
      }
    });
    } catch (e) {
      // Style may not be loaded during HMR — ignore
    }
  }, [config.base_layers, config.measures, config.species, mapReady, mode, styleReady, visibility]);

  const handleKeyboardSelection = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (measurementActiveRef.current) return;
    if (mode !== "planning") return;
    const map = mapRef.current;
    if (!map?.getLayer("measures-line")) return;
    const center = map.project(map.getCenter());
    const feature = map.queryRenderedFeatures(center, { layers: ["measures-line"] })[0];
    if (!feature) return;
    event.preventDefault();
    onMeasureClickRef.current(
      feature as unknown as GeoJSONFeatureCollection["features"][number],
    );
  }, [mode]);

  // Preview instances render a bare, non-interactive container: no
  // `id="map-region"` (that anchor must stay unique to the live map for the
  // skip-link) and no keyboard-selection affordances.
  if (preview) {
    return (
      <div className="relative h-full w-full">
        <div ref={mapContainerRef} className="map-container" />
      </div>
    );
  }

  return (
    <div
      id="map-region"
      data-tour-id="map-region"
      className="relative h-full w-full focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-flima-river"
      role="region"
      aria-label={`${t("map_label")} — ${t(mode === "habitats" ? "habitat_mode" : "planning_mode")}`}
      aria-describedby={mode === "planning" ? "map-keyboard-hint" : undefined}
      tabIndex={0}
      onKeyDown={handleKeyboardSelection}
    >
      {mode === "planning" ? (
        <p id="map-keyboard-hint" className="sr-only">
          {t("map_keyboard_hint")}
        </p>
      ) : null}
      <MapToolbar
        effectiveBasemap={is3D ? "satellite" : basemap}
        is3D={is3D}
        mapOpacity={internalMapOpacity}
        measurementActive={measurementActive}
        measurementPointCount={measurementPoints.length}
        measurementTotalMeters={measurementTotalMeters}
        measurementLastSegmentMeters={measurementLastSegmentMeters}
        onBasemapChange={handleBasemapChange}
        onMapOpacityChange={setInternalMapOpacity}
        onToggle3D={handleToggle3D}
        onToggleMeasurement={handleMeasurementToggle}
        onFinishMeasurement={finishMeasurement}
        onUndoMeasurement={undoMeasurementPoint}
        onClearMeasurement={clearMeasurement}
      />
      <div ref={mapContainerRef} className="map-container" />
    </div>
  );
});
